import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from 'reactstrap';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';

import {
  MOBILE_BREAKPOINT,
  createCardBodyId,
  getTooltipPlacement,
} from './helpers/tooltipHelpers';
import ClauseListTOU from '../TimeOfUse/ClauseListTOU';

/**
 * Shared tooltip component for trade rule cards.
 *
 * This component manages tooltip state, placement logic, and rendering
 * while allowing parent components to control CardBody content via render prop.
 *
 * The component uses shared i18n message IDs for tooltip headers:
 * - 'trade_rule.shared.tooltip.buying_at' (defaultMessage: 'Buying at:')
 * - 'trade_rule.shared.tooltip.selling_at' (defaultMessage: 'Selling at:')
 * @example
 * <TradeRuleTooltip
 *   tradeDirection={TRADE_DIRECTION_BUY}
 *   ruleId="rule-123"
 *   clauseNodes={[...]}
 * >
 *   {(cardBodyId) => (
 *     <CardBody id={cardBodyId} style={{ cursor: 'help' }}>
 *       <CardTitle>My Trade Rule</CardTitle>
 *       <CardText>Content here...</CardText>
 *     </CardBody>
 *   )}
 * </TradeRuleTooltip>
 * @param {object} props - Component props
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.tradeDirection
 *   - Trade direction constant
 * @param {string} props.ruleId - Unique identifier for the trade rule
 * @param {Array<object>} props.clauseNodes - Time-of-use clause data
 * @param {Function} props.children - Render prop function
 * @param {number} [props.mobileBreakpoint=768] - Custom mobile breakpoint in pixels
 * @returns {React.ReactElement} React component with tooltip functionality
 */
function TradeRuleTooltip({
  tradeDirection,
  ruleId,
  clauseNodes,
  children,
  mobileBreakpoint = MOBILE_BREAKPOINT,
}) {
  const intl = useIntl();

  // State management
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < mobileBreakpoint,
  );

  // Handlers
  const toggle = () => setTooltipOpen(!tooltipOpen);

  // Mobile detection effect
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  // Computed values
  const cardBodyId = createCardBodyId(ruleId);
  const tooltipPlacement = getTooltipPlacement(tradeDirection, isMobile);

  // Tooltip header label with hardcoded shared message IDs
  const tooltipHeaderLabel = tradeDirection === TRADE_DIRECTION_BUY
    ? intl.formatMessage({
      id: 'trade_rule.trade_rule_tooltip.tooltip_header_label.buying_at.label',
      defaultMessage: 'Buying at:',
    })
    : intl.formatMessage({
      id: 'trade_rule.trade_rule_tooltip.tooltip_header_label.selling_at.label',
      defaultMessage: 'Selling at:',
    });

  return (
    <>
      {/* Render prop: parent controls CardBody content */}
      {children(cardBodyId, tooltipOpen, toggle)}

      {/* Tooltip: managed by this component */}
      {clauseNodes.length > 0 && (
        <Tooltip
          placement={tooltipPlacement}
          isOpen={tooltipOpen}
          target={cardBodyId}
          toggle={toggle}
          boundariesElement="window"
        >
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {tooltipHeaderLabel}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <ClauseListTOU clauseNodes={clauseNodes} />
            </ul>
          </div>
        </Tooltip>
      )}
    </>
  );
}

TradeRuleTooltip.propTypes = {
  tradeDirection: PropTypes.oneOf([TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL]).isRequired,
  ruleId: PropTypes.string.isRequired,
  clauseNodes: PropTypes.arrayOf(PropTypes.shape({
    monthsOfYear: PropTypes.arrayOf(PropTypes.string),
    daysOfWeek: PropTypes.arrayOf(PropTypes.string),
    timesOfDay: PropTypes.arrayOf(PropTypes.shape({
      start: PropTypes.shape({
        hours: PropTypes.number,
        minutes: PropTypes.number,
        seconds: PropTypes.number,
      }),
      finish: PropTypes.shape({
        hours: PropTypes.number,
        minutes: PropTypes.number,
        seconds: PropTypes.number,
      }),
    })),
  })).isRequired,
  children: PropTypes.func.isRequired,
  mobileBreakpoint: PropTypes.number,
};

export default TradeRuleTooltip;
